import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

/**
 * Default scroll controller using native browser scrolling behavior.
 * Handles `scrollTop`, easing delta over time for smooth lerped animations.
 */
export class StringScrollDefault extends ScrollController {
  /** Unique name identifier for this scroll mode. */
  public readonly name: string = "default";

  /** Last known scroll position for direction detection */
  private previousScrollTop: number = 0;

  private previousScrollTime: number = 0;
  private isScrolling: boolean = false;
  private lastScrollEventTime: number = 0;
  private nativeVelocity: number = 0;
  private nativeVelocityTarget: number = 0;
  private readonly scrollStopDelay = 120;
  private readonly nativeVelocityFollow = 0.2;
  private readonly nativeVelocityDecay = 0.84;
  private readonly nativeVelocityBoost = 2;
  private readonly nativeVelocityDeadzone = 0.25;

  /**
   * Constructs a new instance of the default scroll controller.
   * @param context Shared string system context.
   */
  constructor(context: StringContext) {
    super(context);
  }

  /**
   * Called every animation frame.
   * Applies easing to scroll delta and updates lerped value.
   * Fires `onScrollStop` once movement has settled.
   */
  public onFrame(): void {
    let lerped = 0;

    if (this.context.data.scroll.delta !== 0) {
      const delta = this.context.data.scroll.delta * this.context.data.scroll.speedAccelerate;
      this.context.data.scroll.delta -= delta;
      lerped = delta;
      // this.onCalcUpdate();

      if (Math.abs(lerped) < 0.1) {
        this.context.data.scroll.delta = 0;
        lerped = 0;
      }
    }

    const now = performance.now();

    this.nativeVelocityTarget *= this.nativeVelocityDecay;
    if (Math.abs(this.nativeVelocityTarget) < this.nativeVelocityDeadzone) {
      this.nativeVelocityTarget = 0;
    }

    this.nativeVelocity +=
      (this.nativeVelocityTarget - this.nativeVelocity) * this.nativeVelocityFollow;

    if (Math.abs(this.nativeVelocity) < this.nativeVelocityDeadzone) {
      this.nativeVelocity = 0;
    }

    if (Math.abs(this.nativeVelocity) > Math.abs(lerped)) {
      lerped = this.nativeVelocity;
    }

    this.context.data.scroll.lerped = lerped;

    if (this.isScrolling) {
      const hasSyntheticMomentum =
        this.context.data.scroll.delta !== 0 ||
        this.nativeVelocityTarget !== 0 ||
        this.nativeVelocity !== 0;

      if (!hasSyntheticMomentum && now - this.lastScrollEventTime > this.scrollStopDelay) {
        this.isScrolling = false;
        this.onScrollStop();
        this.clearScrollingClasses();
      }
    }
  }

  /**
   * Called on native scroll event.
   * Syncs the internal scroll state with the current page scroll.
   * @param e Scroll event object (unused).
   */
  public onScroll(e: any): void {
    const now = performance.now();
    const scrollTop = this.context.data.scroll.elementContainer.scrollTop;
    const positionDelta = scrollTop - this.previousScrollTop;
    this.context.data.scroll.current = scrollTop;
    this.context.data.scroll.target = scrollTop;
    this.context.data.scroll.transformedCurrent =
      scrollTop * this.context.data.viewport.transformScale;

    if (positionDelta !== 0) {
      this.updateScrollDirection(positionDelta > 0);

      const timeDelta = this.previousScrollTime === 0 ? 16.6667 : now - this.previousScrollTime;
      const normalizedDelta =
        positionDelta * (16.6667 / Math.max(8, timeDelta)) * this.nativeVelocityBoost;

      this.nativeVelocityTarget = normalizedDelta;

      this.previousScrollTop = scrollTop;
      this.previousScrollTime = now;
    }

    this.triggerScrollRules();

    this.lastScrollEventTime = now;
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.onScrollStart();
    }
  }

  /**
   * Handles wheel input by updating scroll delta for easing.
   * Triggers `onScrollStart` if delta begins from zero.
   * @param e Wheel event.
   */
  public onWheel(e: any): void {
    if (e.deltaY !== 0) {
      if (this.context.data.scroll.delta === 0 && !this.isScrolling) {
        this.isScrolling = true;
        this.onScrollStart();
      }
      this.context.data.scroll.delta += e.deltaY;

      this.lastScrollEventTime = performance.now();
    }
  }

  public deactivate(): void {
    super.deactivate();
    this.isScrolling = false;
    this.lastScrollEventTime = 0;
    this.previousScrollTop = this.context.data.scroll.current;
    this.previousScrollTime = 0;
    this.nativeVelocity = 0;
    this.nativeVelocityTarget = 0;
  }

  public scrollTo(position: number, immediate?: boolean) {
    this.context.data.scroll.target = position;
    this.context.data.scroll.current = position;
    this.context.data.scroll.delta = 0;
    this.context.data.scroll.lerped = 0;
    this.nativeVelocity = 0;
    this.nativeVelocityTarget = 0;
    this.previousScrollTop = position;
    this.previousScrollTime = 0;
    this.triggerScrollRules();

    if (this._scrollDirection === "vertical") {
      this.context.data.scroll.scrollContainer?.scrollTo({
        top: position,
        left: 0,
        behavior: immediate ? "auto" : "smooth",
      });
    } else {
      this.context.data.scroll.scrollContainer?.scrollTo({
        left: position,
        top: 0,
        behavior: immediate ? "auto" : "smooth",
      });
    }
  }
}
